import { RootState } from "..";
import {
	ICameraFilterDetails,
	IDeviceDetails,
	IGetAllCamerasListRequestData,
	IGetAllDevicesRequestData,
	IGetClientDetailsResponseData,
	IGetFrameImageRequestData,
	IGetFrameImageResponseData,
	IGetUserSettingsResponseData,
	IGetVehicleModelsRequestData,
	INodeDetails,
	IUpdateUserSettingsRequestData,
	IVehicleBrandDetails,
	IVehicleCategoryDetails,
	IVehicleClassDetails,
	IVehicleColorDetails,
	IVehicleModelDetails,
	IVehicleTypeDetails,
	IViolationDetails
} from "../../types";
import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import { addCachedImageData, updateCachedImageTimestamp } from "./auth.reducer";
import { queries } from "./queries.reducer";

const settings = queries.injectEndpoints({
	endpoints: (builder) => ({
		// GET CLIENTS LIST
		getClientsList: builder.query<IGetClientDetailsResponseData[], void | null>({
			query: () => ({
				url: "/transpo_tracker/analytics/client",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET NODES LIST
		getNodesList: builder.query<INodeDetails[], void | null>({
			query: () => ({
				url: "/transpo_tracker/analytics/node",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET VEHICLE CATEGORIES
		getVehicleCategories: builder.query<IVehicleCategoryDetails[], void>({
			query: () => ({
				url: "/analytics/vehiclecategory",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET VEHICLE COLORS
		getVehicleColors: builder.query<IVehicleColorDetails[], void>({
			query: () => ({
				url: "/analytics/vehiclecolor",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET FRAME IMAGE
		getFrameImage: builder.mutation<IGetFrameImageResponseData, IGetFrameImageRequestData>({
			queryFn: async (requestData, api, _extraOptions, baseQuery) => {
				const cacheEntryKey = `${requestData.image_type}|${requestData.message_id}`;

				const cachedImages = (api.getState() as RootState).auth.cachedImagesMap;

				if (cachedImages[cacheEntryKey]) {
					api.dispatch(updateCachedImageTimestamp({ key: cacheEntryKey, timestamp: +new Date() }));
					return { data: cachedImages[cacheEntryKey].data };
				}

				try {
					const response = await baseQuery({
						url: "/cameraconfig/get_image",
						body: requestData,
						headers: getAPIHeaderOptions(true),
						method: "POST"
					});

					if (response.data) {
						const responseData = response.data as IGetFrameImageResponseData;

						api.dispatch(
							addCachedImageData({
								key: cacheEntryKey,
								data: responseData,
								timestamp: +new Date()
							})
						);

						return { data: responseData };
					}

					return { error: { error: "Something went wrong", status: "CUSTOM_ERROR" } };
				} catch (error) {
					return { error: { error: "Something went wrong", status: "CUSTOM_ERROR" } };
				}
			}
		}),

		// GET VIOLATIONS LIST
		getViolationsList: builder.query<IViolationDetails[], void>({
			query: () => ({
				url: "/analytics/violationtype",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET USER SETTINGS
		getUserSettings: builder.query<IGetUserSettingsResponseData, void | null>({
			query: () => ({
				url: `/auth/rbac/users/me/`,
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// UPDATE USER SETTINGS
		updateUserSettings: builder.mutation<unknown, IUpdateUserSettingsRequestData>({
			query: (requestData) => ({
				url: `/auth/users/${requestData.username}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET DEVICES LIST
		getDevicesList: builder.query<IDeviceDetails[], IGetAllDevicesRequestData>({
			query: (requestData) => ({
				url: "/devices/device-filter",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET VEHICLE CLASSES LIST
		getVehicleClassesList: builder.query<IVehicleClassDetails[], void>({
			query: () => ({
				url: "/analytics/vehicleclass",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET VEHICLE TYPES LIST
		getVehicleTypesList: builder.query<IVehicleTypeDetails[], void>({
			query: () => ({
				url: "/analytics/vehicle_type",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET VEHICLE BRANDS LIST
		getVehicleBrandsList: builder.query<IVehicleBrandDetails[], void>({
			query: () => ({
				url: "/analytics/vehiclebrand",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET VEHICLE MODELS LIST
		getVehicleModelsList: builder.query<IVehicleModelDetails[], IGetVehicleModelsRequestData>({
			query: (requestData) => ({
				url: "/analytics/vehiclemodel",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET ALL CAMERAS LIST
		getAllCamerasList: builder.query<ICameraFilterDetails[], IGetAllCamerasListRequestData>({
			query: (requestData) => ({
				url: "/devices/camera-list",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		})
	})
});

export const {
	// GET CLIENTS LIST
	useGetClientsListQuery,
	// GET NODES LIST
	useGetNodesListQuery,
	// GET VEHICLE CATEGORIES
	useGetVehicleCategoriesQuery,
	// GET VEHICLE COLORS
	useGetVehicleColorsQuery,
	// GET FRAME IMAGE
	useGetFrameImageMutation,
	// GET VIOLATIONS LIST
	useGetViolationsListQuery,
	// GET USER SETTINGS
	useGetUserSettingsQuery,
	// UPDATE USER SETTINGS
	useUpdateUserSettingsMutation,
	// GET DEVICES LIST
	useGetDevicesListQuery,
	// GET VEHICLE CLASSES LIST
	useGetVehicleClassesListQuery,
	// GET VEHICLE TYPES LIST
	useGetVehicleTypesListQuery,
	// GET VEHICLE BRANDS LIST
	useGetVehicleBrandsListQuery,
	// GET VEHICLE MODELS LIST
	useGetVehicleModelsListQuery,
	// GET ALL CAMERAS LIST
	useGetAllCamerasListQuery
} = settings;
